/**
 * Página de detalle del destilado de una sesión (/distill/:sessionId).
 *
 * Muestra TODO lo que el destilado produjo, no sólo memory_facts: vocabulario,
 * patrones de error, correcciones turn-by-turn, métricas/CEFR, objetivos de
 * escenario y memory facts. Combina tres fuentes:
 *   - GET /sessions/:id/distill   → métricas del job (modo, costo, duración)
 *   - GET /sessions/:id/learning  → tablas dedicadas de aprendizaje
 *   - GET /users/:userId/memory/facts?session_id= → memory facts (descifrados)
 *
 * Se abre desde la lista /distill (click en fila) o desde el SessionDetail
 * (botón "Ver detalle del destilado").
 */
import { type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchSessionDistill } from '../../api/admin/distill.js';
import {
  fetchSessionLearning,
  type LearningCorrectionItem,
  type LearningErrorPatternItem,
  type LearningMetrics,
  type LearningScenarioObjectiveItem,
  type LearningVocabItem,
} from '../../api/admin/learning.js';
import { fetchUserMemoryFacts, type MemoryFactItem } from '../../api/admin/user-memory.js';
import {
  formatDurationMs,
  nanoToUsdCompact,
  formatDateTime,
  shortId,
} from '../../lib/format.js';
import { preferRawCost } from '../../lib/cost-display.js';
import '../../styles/distill-detail.css';

/** numeric pg llega como string "0.85"; lo formateamos a "85%". */
function pct(raw: string | null): string | null {
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? `${Math.round(n * 100)}%` : null;
}

function SectionCard({
  title,
  count,
  spanFull,
  children,
}: {
  title: string;
  count?: number;
  spanFull?: boolean;
  children: React.ReactNode;
}): ReactElement {
  return (
    <section className={`card learn-card${spanFull ? ' span-full' : ''}`}>
      <div className="learn-card__head">
        <span className="section-eyebrow">{title}</span>
        {count !== undefined ? <span className="learn-card__count">{count}</span> : null}
      </div>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }): ReactElement {
  return <div className="t-meta" style={{ padding: '4px 0' }}>{children}</div>;
}

function VocabSection({ items }: { items: LearningVocabItem[] }): ReactElement {
  return (
    <SectionCard title="Vocabulario" count={items.length}>
      {items.length === 0 ? (
        <Empty>No se registró vocabulario en esta sesión.</Empty>
      ) : (
        <div className="learn-list">
          {items.map((v) => (
            <div key={v.id} className="learn-item">
              <div className="learn-item__top">
                <span className="learn-item__primary">{v.text}</span>
                {v.cefr ? <span className="kind-pill">{v.cefr}</span> : null}
                {v.entryType !== 'word' ? (
                  <span className="t-meta mono">{v.entryType}</span>
                ) : null}
                {v.topic ? <span className="learn-conf">{v.topic}</span> : null}
              </div>
              {v.translation ? <p className="learn-item__secondary">{v.translation}</p> : null}
              {v.example ? <p className="learn-item__quote">“{v.example}”</p> : null}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function ErrorsSection({ items }: { items: LearningErrorPatternItem[] }): ReactElement {
  return (
    <SectionCard title="Errores" count={items.length}>
      {items.length === 0 ? (
        <Empty>Sin patrones de error registrados.</Empty>
      ) : (
        <div className="learn-list">
          {items.map((e) => {
            const conf = pct(e.confidence);
            return (
              <div key={e.id} className="learn-item">
                <div className="learn-item__top">
                  <span className="learn-item__primary">{e.pattern}</span>
                  {e.dimension ? <span className="kind-pill">{e.dimension}</span> : null}
                  {e.category ? <span className="t-meta mono">{e.category}</span> : null}
                  {conf ? <span className="learn-conf">conf {conf}</span> : null}
                </div>
                {e.example ? <p className="learn-item__quote">“{e.example}”</p> : null}
                {e.suggestion ? (
                  <p className="learn-item__secondary">💡 {e.suggestion}</p>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

function CorrectionsSection({ items }: { items: LearningCorrectionItem[] }): ReactElement {
  return (
    <SectionCard title="Correcciones" count={items.length} spanFull>
      {items.length === 0 ? (
        <Empty>Sin correcciones registradas.</Empty>
      ) : (
        <div className="learn-list">
          {items.map((c) => (
            <div key={c.id} className="learn-item">
              <div className="learn-item__top">
                <span className={`sev sev-${c.severity}`}>{c.severity}</span>
                {c.turnIndex !== null ? (
                  <span className="t-meta mono">turn {c.turnIndex}</span>
                ) : null}
                {c.category ? <span className="t-meta">{c.category}</span> : null}
              </div>
              <div className="correction-pair">
                <span className="wrong">{c.userUtterance}</span>
                <span className="arrow">→</span>
                <span className="right">{c.correctedForm}</span>
              </div>
              {c.explanation ? <p className="learn-item__secondary">{c.explanation}</p> : null}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function MetricsSection({ metrics }: { metrics: LearningMetrics | null }): ReactElement {
  return (
    <SectionCard title="CEFR / métricas">
      {!metrics ? (
        <Empty>Sin métricas de sesión (no es una sesión de idiomas o no se destiló).</Empty>
      ) : (
        <div className="metrics-grid">
          <div className="metric">
            <span className="k">CEFR estimado</span>
            <span className="v">{metrics.cefrEstimate ?? '—'}</span>
          </div>
          <div className="metric">
            <span className="k">Modo</span>
            <span className="v" style={{ fontSize: 13 }}>{metrics.mode}</span>
          </div>
          {metrics.paWordsEvaluated > 0 ? (
            <>
              <div className="metric">
                <span className="k">PA accuracy</span>
                <span className="v">{metrics.paAvgAccuracy ?? '—'}</span>
              </div>
              <div className="metric">
                <span className="k">PA palabras</span>
                <span className="v">{metrics.paWordsEvaluated}</span>
              </div>
            </>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
}

function ObjectivesSection({
  items,
}: {
  items: LearningScenarioObjectiveItem[];
}): ReactElement | null {
  if (items.length === 0) return null;
  return (
    <SectionCard title="Objetivos de escenario" count={items.length}>
      <div className="learn-list">
        {items.map((o) => (
          <div key={o.id} className="learn-item">
            <div className="objective-row">
              <span className={`mark ${o.met ? 'met' : 'unmet'}`}>{o.met ? '✓' : '✗'}</span>
              <span className="learn-item__primary">{o.objectiveId}</span>
            </div>
            {o.evidence ? <p className="learn-item__quote">“{o.evidence}”</p> : null}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function FactsSection({ items }: { items: MemoryFactItem[] }): ReactElement {
  return (
    <SectionCard title="Memory facts" count={items.length}>
      {items.length === 0 ? (
        <Empty>Este destilado no escribió memory facts (esperable en sesiones de idioma).</Empty>
      ) : (
        <div className="learn-list">
          {items.map((f) => (
            <div key={f.id} className={`learn-item${f.disabledAt ? ' is-disabled' : ''}`}>
              <div className="learn-item__top">
                <span className="kind-pill">{f.kind}</span>
                <span className="t-meta mono">{f.scope}</span>
                {f.disabledAt ? <span className="badge b-warn">desactivado</span> : null}
              </div>
              <p className="learn-item__secondary">{f.body}</p>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

export function DistillDetailPage(): ReactElement {
  const { sessionId } = useParams<{ sessionId: string }>();
  const sid = sessionId ?? '';

  const learning = useQuery({
    queryKey: ['admin', 'session-learning', sid],
    queryFn: () => fetchSessionLearning(sid),
    enabled: Boolean(sid),
  });

  const distill = useQuery({
    queryKey: ['admin', 'distill', sid],
    queryFn: () => fetchSessionDistill(sid),
    enabled: Boolean(sid),
  });

  const userId = learning.data?.meta.userId;
  const facts = useQuery({
    queryKey: ['admin', 'distill-facts', userId, sid],
    queryFn: () => fetchUserMemoryFacts(userId!, { session_id: sid, include_disabled: true, limit: 200 }),
    enabled: Boolean(userId),
  });

  const job = distill.data?.distill ?? null;
  const meta = learning.data?.meta;

  return (
    <>
      <div className="page-header">
        <div>
          <Link to="/distill" className="t-meta">← Destilados</Link>
          <h1 className="page-title" style={{ marginTop: 4 }}>
            Destilado · sesión <code className="mono">{shortId(sid, 10)}</code>
          </h1>
          <p className="page-sub" style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {job ? (
              <span className={job.mode === 'agentic' ? 'badge b-warn' : 'badge b-info'}>
                {job.mode}
              </span>
            ) : null}
            {meta ? (
              <span>
                <Link to={`/users/${meta.userId}`}>{meta.userEmail}</Link>
                {meta.moduleId ? <> · <code className="mono">{meta.moduleId}</code></> : null}
              </span>
            ) : null}
            {meta?.startedAt ? <span title={meta.startedAt}>{formatDateTime(meta.startedAt)}</span> : null}
            {job ? (
              <span>
                {formatDurationMs(job.totalDurationMs)} ·{' '}
                {nanoToUsdCompact(preferRawCost(job.totalCostRawNano, job.totalCostNano))}
              </span>
            ) : null}
            <Link to={`/sessions/${sid}?span=${job?.jobSpanId ?? ''}`} className="t-meta">
              ver waterfall →
            </Link>
          </p>
        </div>
      </div>

      {learning.isPending ? (
        <div className="loading">Cargando…</div>
      ) : learning.isError ? (
        <div className="loading">Error: {learning.error.message}</div>
      ) : (
        <div className="distill-detail-grid">
          <VocabSection items={learning.data.vocab} />
          <ErrorsSection items={learning.data.errorPatterns} />
          <MetricsSection metrics={learning.data.metrics} />
          <ObjectivesSection items={learning.data.scenarioObjectives} />
          <CorrectionsSection items={learning.data.corrections} />
          <FactsSection items={facts.data?.items ?? []} />
        </div>
      )}
    </>
  );
}
